import * as _ from 'lodash';
import * as Edge from '../../core';
import { ExerciseMetadata, ExerciseMetadataService } from '../../services/exerciseMetadataService';
import { getColorRange } from '../../pages/training/helper';

export interface SkillScoreCard {
	exerciseTypeId: Edge.Models.ExerciseTypeId;
	title: string;
	score: number;
	percentile: number;
	statusLabel: string;
	color: string;
	Icon: ExerciseMetadata['logo'];
}

export const getOrdinalSuffix = (value: number): string => {
	if (value === 0) {
		return '';
	}
	if (value >= 11 && value <= 13) {
		return 'th';
	}
	const formattedValue = Math.round(value) % 100;
	switch (formattedValue % 10) {
		case 1:
			return 'st';
		case 2:
			return 'nd';
		case 3:
			return 'rd';
		default:
			return 'th';
	}
};

export const getSkillScoreCards = (session: Edge.Models.BaseSessionResponse): SkillScoreCard[] => {
	const cards: SkillScoreCard[] = [];

	session.exerciseResults
		.filter((result) => result.complete)
		.forEach((result) => {
			const metadata = ExerciseMetadataService.getMetadata(result);
			if (!metadata) {
				return;
			}
			const percentile = result.explanationPercentile || 0;
			const range = getColorRange(percentile * 100);
			cards.push({
				exerciseTypeId: result.exerciseTypeId,
				title: metadata.title.split(' ')[0],
				score: Math.round(
					(result.explanationPercentile !== undefined && result.explanationPercentile !== null
						? result.explanationPercentile
						: result.correctPercent || 0) * 100
				),
				percentile,
				statusLabel: result.explanationRankingText || range.label,
				color: range.color,
				Icon: metadata.logo,
			});
		});

	return _.sortBy(cards, (card) => {
		const metadata = ExerciseMetadataService.getMetadata({ exerciseTypeId: card.exerciseTypeId });
		return (metadata && metadata.defaultOrder) || 0;
	});
};

const getWeakestSkills = (cards: SkillScoreCard[], count = 2): string[] => {
	return _.sortBy(cards, (card) => card.percentile)
		.slice(0, count)
		.map((card) => card.title.toLowerCase());
};

export const buildKeyTakeaway = (cards: SkillScoreCard[]): { summary: string; encouragement: string } => {
	const encouragement = 'Consistent training in these areas can lead to meaningful improvements over time.';
	const weakest = getWeakestSkills(cards, 2);
	if (weakest.length === 0) {
		return {
			encouragement,
			summary:
				'Your Edge Test results give us a clear picture of where to focus your training for the biggest gains.',
		};
	}
	const focusPhrase = weakest.length === 1 ? weakest[0] : `${weakest[0]} and ${weakest[1]}`;
	return {
		encouragement,
		summary: `Your biggest opportunities are in ${focusPhrase}.`,
	};
};
